import { PayloadAction } from "@reduxjs/toolkit";
import { createApi, createSlice } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { LoginState } from "./types";
import { baseQuery, endpoints, formatErrors } from "utils/api/endpoints";

export interface LoginResponse {
  data: {
    firstName: string;
    lastName: string;
    phone: string;
    alternatePhone: string;
    securityAnswer: string;
    enrollmentId: string | null;
    networkType: number;
    twoFaCode: string;
    twoFaGeneratedAt: string;
    _id: string;
    email: string;
    securityQuestion: number;
    createdAt: string;
    updatedAt: string;
    stripeCustomerId: string;
    __v: number;
    phoneNumbers: string[];
    passwordResetOtp: string | null;
    isFixedPhone: boolean;
    hasTrialDigitalSecurityPlan?: boolean;
  };
  status: number;
  statusText: string;
  meta: {
    requestId: string;
    url: string;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface VerifyOTPRequest {
  phone: string;
  twoFaCode: string;
}

export interface VerifyOTPResponse {
  status: number;
  message: string;
}

export interface SendCodeToPhoneRequest {
  phone: string;
}

export interface SendCodeToPhoneResponse {
  status: number;
  message: string;
  data?: {
    twoFaCode?: string;
  };
}

export const initialState: LoginState = {
  user: null,
};

const slice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setUser: (
      state,
      action: PayloadAction<{
        user: LoginResponse["data"] | null;
        rememberDevice: boolean;
      }>
    ) => {
      state.user = action.payload.user;

      // Clear both storages first
      localStorage.removeItem("vle-user");
      sessionStorage.removeItem("vle-user");

      // Store user data based on rememberDevice preference
      if (action.payload.user) {
        if (action.payload.rememberDevice) {
          localStorage.setItem("vle-user", JSON.stringify(action.payload.user));
        } else {
          sessionStorage.setItem(
            "vle-user",
            JSON.stringify(action.payload.user)
          );
        }
      }
    },
  },
});

export const api = createApi({
  reducerPath: "loginApi",
  baseQuery,
  endpoints: (build) => ({
    login: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => {
        return {
          ...endpoints.login,
          body,
        };
      },
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    verifyOTP: build.mutation<VerifyOTPResponse, VerifyOTPRequest>({
      query: (body) => {
        return {
          ...endpoints.verifyOTP,
          body,
        };
      },
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    sendCodeToPhone: build.mutation<
      SendCodeToPhoneResponse,
      SendCodeToPhoneRequest
    >({
      query: (body) => {
        return {
          ...endpoints.sendCodeToPhone,
          body,
        };
      },
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    getProfile: build.query<any, any>({
      query: (params) => {
        return {
          ...endpoints.getProfile,
          params,
        };
      },
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
  }),
});

export const { actions: Actions, reducer } = slice;

export const useLoginSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return { actions: slice.actions, ...api };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
